import { useState, useCallback, useEffect } from "react";
import { getAllPolls, type Poll } from "../../lib/pollStorage";
import { getConversations } from "../../lib/storage/ConversationStorageSimple";
import { useAuth } from "../../contexts/AuthContext";
import { logger } from "../../lib/logger";
import { getCurrentUserId } from "../../lib/guestIdentityService";

interface UseWorkspaceDataProps {
  chatKey: string;
  currentPoll: Poll | null;
  conversationsRefreshKey: number;
}

export function useWorkspaceData({
  chatKey,
  currentPoll,
  conversationsRefreshKey,
}: UseWorkspaceDataProps) {
  const { user } = useAuth();
  const [recentPolls, setRecentPolls] = useState<Poll[]>([]);
  const [conversations, setConversations] = useState<ReturnType<typeof getConversations>>([]);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);

  // Fonction pour charger les données
  const loadData = useCallback(() => {
    try {
      // Charger sondages
      const polls = getAllPolls();

      // Dédupliquer par ID (garder le plus récent en cas de doublon)
      const uniquePolls = Array.from(
        polls
          .reduce((map, poll) => {
            const existing = map.get(poll.id);
            if (
              !existing ||
              new Date(poll.updated_at || poll.created_at) >
                new Date(existing.updated_at || existing.created_at)
            ) {
              map.set(poll.id, poll);
            }
            return map;
          }, new Map<string, Poll>())
          .values(),
      );

      const sorted = uniquePolls
        .filter((p) => p.created_at)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 5);
      setRecentPolls(sorted);

      // Charger conversations et filtrer par utilisateur
      const allConvs = getConversations();
      // Filtrer les conversations pour ne garder que celles du créateur actuel
      // Si connecté : garder celles avec userId === user.id
      // Si invité : garder celles avec userId === "guest" ou undefined (rétrocompatibilité)
      const filteredConvs = allConvs.filter((conv) => {
        if (user?.id) {
          // Mode connecté : garder seulement les conversations de l'utilisateur
          return conv.userId === user.id;
        } else {
          // Mode invité : garder seulement les conversations invitées
          return conv.userId === "guest" || conv.userId === undefined;
        }
      });
      const sortedConvs = filteredConvs.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      );
      setConversations(sortedConvs);
    } catch (error) {
      logger.error("Erreur chargement données", error);
    }
  }, [user?.id]);

  // Charger les sondages récents et conversations
  // Se recharge quand on change de conversation (chatKey change), quand un poll est mis à jour, ou quand les clés de rafraîchissement changent
  useEffect(() => {
    loadData();
  }, [chatKey, currentPoll, loadData, conversationsRefreshKey, internalRefreshKey]);

  // Écouter les événements de changement de conversations (suppression, création, etc.)
  useEffect(() => {
    const handleConversationsChanged = (event?: CustomEvent) => {
      logger.info("🔄 Conversations changées, rechargement du sidebar", "conversation", {
        action: event?.detail?.action,
        conversationId: event?.detail?.conversationId,
      });
      // Petit délai pour s'assurer que localStorage est bien mis à jour
      // puis forcer le rechargement en incrémentant la clé de rafraîchissement
      setTimeout(() => {
        setInternalRefreshKey((prev) => prev + 1);
      }, 0);
    };

    window.addEventListener("conversationsChanged", handleConversationsChanged as EventListener);
    return () =>
      window.removeEventListener(
        "conversationsChanged",
        handleConversationsChanged as EventListener,
      );
  }, []);

  return {
    recentPolls,
    conversations,
    refreshData: () => setInternalRefreshKey((prev) => prev + 1),
  };
}
